import { Button, Dialog, EmptyState, notify, TextAreaField, TextField, useConfirm } from "@flexfreight/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { Link } from "react-router";
import { api } from "../../api/client";
import { useConsoleMe } from "../../auth/context";
import { errorText, previewDoc, TEMPLATES_KEY, when, type Rendered, type Template, type TemplateList } from "./emailApi";

/** Email > Templates (A.25): name, subject and body with {{variables}} filled from real records when used. */
export function EmailTemplatesPage() {
  const me = useConsoleMe();
  const canEdit = me.capabilities.includes("email:send");
  const q = useQuery({ queryKey: TEMPLATES_KEY, queryFn: () => api<TemplateList>("/v1/console/email/templates") });
  const [editing, setEditing] = useState<Template | "new" | null>(null);
  const rows = q.data?.templates ?? [];
  return (
    <section className="ffa-page">
      <header className="ffa-head">
        <div>
          <h1 className="ffa-page__title">Email templates</h1>
          <p className="ffa-head__sub">Reusable messages for the Inbox. Variables like {"{{customer.name}}"} are filled from the customer, quote, shipment or ticket you link, and are escaped so they can't add markup.</p>
        </div>
        <div className="ffa-head__actions">
          <Link className="ffx-btn" to="/inbox">Back to Inbox</Link>
          {canEdit ? <Button variant="primary" onClick={() => setEditing("new")}>New template</Button> : null}
        </div>
      </header>
      {q.isPending ? (
        <p className="ffa-muted">Loading...</p>
      ) : q.isError ? (
        <p role="alert">{errorText(q.error)}</p>
      ) : rows.length === 0 ? (
        <EmptyState title="No templates yet" body="Add one for the replies you write most: quote follow-ups, pickup confirmations, BOL requests." actions={canEdit ? <Button variant="primary" onClick={() => setEditing("new")}>New template</Button> : undefined} />
      ) : (
        <ul className="ffa-tpls">
          {rows.map((t) => (
            <li key={t.id} className="ffx-card ffa-card ffa-tpl">
              <div className="ffa-card__headrow">
                <h2 className="ffa-card__title">{t.name}</h2>
                <span className="ffa-small ffa-muted">Updated {when.format(new Date(t.updatedAt))}</span>
              </div>
              <p className="ffa-small"><strong>Subject:</strong> {t.subject}</p>
              <p className="ffa-small ffa-muted">{t.variables.length ? t.variables.map((v) => `{{${v}}}`).join(" ") : "No variables"}</p>
              {canEdit ? <Button size="sm" onClick={() => setEditing(t)}>Edit</Button> : null}
            </li>
          ))}
        </ul>
      )}
      {editing && q.data ? <TemplateDialog t={editing === "new" ? null : editing} variables={q.data.variables} onClose={() => setEditing(null)} /> : null}
    </section>
  );
}

function TemplateDialog({ t, variables, onClose }: { t: Template | null; variables: TemplateList["variables"]; onClose: () => void }) {
  const qc = useQueryClient();
  const [name, setName] = useState(t?.name ?? "");
  const [subject, setSubject] = useState(t?.subject ?? "");
  const [body, setBody] = useState(t?.bodyHtml ?? "");
  const [preview, setPreview] = useState<Rendered | null>(null);
  const area = useRef<HTMLTextAreaElement>(null);
  const { confirm, dialog } = useConfirm();
  const done = () => {
    void qc.invalidateQueries({ queryKey: TEMPLATES_KEY });
    onClose();
  };
  const save = useMutation({
    mutationFn: () => api<Template>(t ? `/v1/console/email/templates/${t.id}` : "/v1/console/email/templates", { method: t ? "PUT" : "POST", body: { name: name.trim(), subject: subject.trim(), bodyHtml: body } }),
    onSuccess: () => {
      notify.success("Template saved");
      done();
    },
    onError: (e) => notify.error("Couldn't save", errorText(e)),
  });
  const remove = useMutation({
    mutationFn: () => api(`/v1/console/email/templates/${t!.id}`, { method: "DELETE" }),
    onSuccess: () => {
      notify.success("Template deleted");
      done();
    },
    onError: (e) => notify.error("Couldn't delete", errorText(e)),
  });
  const show = useMutation({
    mutationFn: () => api<Rendered>("/v1/console/email/templates/preview", { method: "POST", body: { subject, bodyHtml: body || " " } }),
    onSuccess: setPreview,
    onError: (e) => notify.error("Couldn't preview", errorText(e)),
  });
  const insert = (key: string) => {
    const el = area.current;
    const token = `{{${key}}}`;
    const at = el?.selectionStart ?? body.length;
    setBody(body.slice(0, at) + token + body.slice(el?.selectionEnd ?? at));
  };
  const valid = name.trim() !== "" && subject.trim() !== "" && body.trim() !== "";
  return (
    <Dialog
      open
      onClose={onClose}
      title={t ? "Edit template" : "New template"}
      description="Preview shows staff fields filled in; record fields fill when you use the template on a message."
      footer={
        <>
          {t ? <Button variant="ghost" onClick={async () => { if (await confirm({ title: `Delete "${t.name}"?`, body: "Drafts already written from it keep their text.", confirmLabel: "Delete", danger: true })) remove.mutate(); }}>Delete</Button> : <Button variant="ghost" onClick={onClose}>Cancel</Button>}
          <Button onClick={() => show.mutate()} loading={show.isPending}>Preview</Button>
          <Button variant="primary" disabled={!valid} loading={save.isPending} onClick={() => save.mutate()}>Save</Button>
        </>
      }
    >
      <form className="ffa-form" onSubmit={(e) => { e.preventDefault(); if (valid) save.mutate(); }}>
        <TextField label="Name" value={name} onChange={(e) => setName(e.target.value)} maxLength={120} />
        <TextField label="Subject" value={subject} onChange={(e) => setSubject(e.target.value.replace(/[\r\n]+/g, " "))} maxLength={255} />
        <TextAreaField ref={area} label="Body" value={body} onChange={(e) => setBody(e.target.value)} rows={9} hint="Plain text, or basic HTML (paragraphs, bold, links, lists). Scripts, styles and event handlers are removed." />
        <div className="ffa-varlist" role="group" aria-label="Insert a variable">
          {variables.map((v) => (
            <button key={v.key} type="button" className="ffa-chip ffa-chip--btn" title={v.label} onClick={() => insert(v.key)}>{`{{${v.key}}}`}</button>
          ))}
        </div>
      </form>
      {preview ? (
        <figure className="ffa-compose__preview">
          <figcaption className="ffa-small"><strong>Subject:</strong> {preview.subject}</figcaption>
          <iframe title="Template preview" sandbox="" srcDoc={previewDoc(preview.bodyHtml)} />
        </figure>
      ) : null}
      {dialog}
    </Dialog>
  );
}
